import React, { useState, useEffect, useRef, useMemo } from 'react';

/* ═══════════════════════════════════════
   CONSTANTS
   ═══════════════════════════════════════ */
const LAUNCH_DAYS = 60;

const FEATURES = [
  'AI Voice Recording',
  'Live Transcription',
  'Speaker ID',
  'Noise Cancellation',
  'Biometric Analysis',
  'Cloud Archive',
];

const GOLD_PALETTE = [
  '#c9a84c', '#d4b86a', '#b89640',
  '#a87fd4', '#8a6d24', '#e8cc7a',
];

/* ═══════════════════════════════════════
   STYLES (injected)
   ═══════════════════════════════════════ */
const styles = `
@import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,700;0,900;1,700&family=Outfit:wght@300;400;500;600&display=swap');

:root {
  --ink:          #faf9f6;
  --deep:         #f2efe8;
  --glass:        rgba(0,0,0,0.03);
  --glass-bd:     rgba(0,0,0,0.08);
  --gold:         #a07820;
  --gold-lt:      #c9a84c;
  --gold-dk:      #6b5010;
  --violet:       #5a4080;
  --cream:        #1a1612;
  --cream-dim:    rgba(26,22,18,0.55);
  --cream-faint:  rgba(26,22,18,0.25);
  --ff-display:   'Playfair Display', Georgia, serif;
  --ff-body:      'Outfit', system-ui, sans-serif;
}

*, *::before, *::after { margin:0; padding:0; box-sizing:border-box; }

/* KEYFRAMES */
@keyframes slowRotate    { to { transform: rotate(360deg); } }
@keyframes slowRotateRev { to { transform: rotate(-360deg); } }
@keyframes breathe {
  0%,100% { transform: scale(1);    opacity: .5; }
  50%     { transform: scale(1.12); opacity: .9;  }
}
@keyframes shimmer {
  0%   { background-position: -200% center; }
  100% { background-position:  200% center; }
}
@keyframes fadeUp {
  from { opacity:0; transform:translateY(38px); }
  to   { opacity:1; transform:translateY(0);    }
}
@keyframes fadeIn {
  from { opacity:0; }
  to   { opacity:1; }
}
@keyframes scanLine {
  0%   { top:-4px; }
  100% { top:100%; }
}
@keyframes flipCard {
  0%  { transform:scaleY(1); }
  50% { transform:scaleY(0.04); }
  100%{ transform:scaleY(1); }
}
@keyframes orbitPulse {
  0%,100%{ box-shadow:0 0 0 0   rgba(160,120,32,.4); }
  50%    { box-shadow:0 0 0 14px rgba(160,120,32,0); }
}
@keyframes inputGlow {
  0%   { box-shadow:0 0 0 0   rgba(160,120,32,.3); }
  100% { box-shadow:0 0 0 6px rgba(160,120,32,0);  }
}
@keyframes particleDrift {
  0%  { transform:translateY(0) translateX(0); opacity:0; }
  15% { opacity:.5; }
  85% { opacity:.2; }
  100%{ transform:translateY(-110vh) translateX(var(--dx)); opacity:0; }
}
@keyframes waveBar {
  0%,100%{ transform:scaleY(.25); }
  50%    { transform:scaleY(1);   }
}

/* WRAPPER */
.cs-root {
  min-height: 100vh;
  background: var(--ink);
  font-family: var(--ff-body);
  color: var(--cream);
  overflow: hidden;
  position: relative;
}
.cs-root::after {
  content:'';
  position:fixed; inset:0;
  background:
    radial-gradient(ellipse 80% 60% at 50%   0%,  rgba(160,120,32,.07) 0%,transparent 60%),
    radial-gradient(ellipse 60% 40% at 20% 100%,  rgba(160,120,32,.06) 0%,transparent 60%),
    radial-gradient(ellipse 50% 80% at 90%  50%,  rgba(90,64,128,.04) 0%,transparent 60%);
  pointer-events:none; z-index:0;
}

/* NOISE TEXTURE OVERLAY */
.cs-root::before {
  content:'';
  position:fixed; inset:0;
  background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)' opacity='0.04'/%3E%3C/svg%3E");
  background-size:200px 200px;
  opacity:0.6;
  pointer-events:none; z-index:0;
}

/* GRID */
.cs-grid {
  position:fixed; inset:0; z-index:1; pointer-events:none;
  background-image:
    linear-gradient(rgba(160,120,32,.012) 1px, transparent 1px),
    linear-gradient(90deg,rgba(160,120,32,.012) 1px, transparent 1px);
  background-size: 90px 90px;
  mask-image: radial-gradient(ellipse 65% 65% at 50% 50%, black 20%, transparent 100%);
}

/* SCAN LINE */
.cs-scanline {
  position:fixed; left:0; right:0; height:1px;
  background:linear-gradient(90deg,transparent,rgba(160,120,32,.05),transparent);
  animation:scanLine 9s linear infinite;
  z-index:2; pointer-events:none;
}

/* PARTICLES */
.cs-particles { position:fixed; inset:0; pointer-events:none; z-index:1; overflow:hidden; }
.cs-particle  { position:absolute; bottom:-10px; border-radius:99px; opacity:0; animation:particleDrift linear infinite; }

/* ORBITING RINGS */
.cs-rings {
  position:fixed; top:50%; left:50%;
  transform:translate(-50%,-50%);
  z-index:1; pointer-events:none;
}
.cs-ring {
  position:absolute; border-radius:50%; border:1px solid;
  top:50%; left:50%; transform:translate(-50%,-50%);
}
.cs-ring-1 { width:500px;  height:500px;  border-color:rgba(160,120,32,.09);  animation:slowRotate    60s linear infinite; }
.cs-ring-2 { width:760px;  height:760px;  border-color:rgba(90,64,128,.05);   animation:slowRotateRev 90s linear infinite; }
.cs-ring-3 { width:1060px; height:1060px; border-color:rgba(160,120,32,.04);  animation:slowRotate   125s linear infinite; }
.cs-ring-dot {
  position:absolute; width:5px; height:5px; border-radius:50%;
  background:var(--gold); box-shadow:0 0 8px var(--gold);
  top:50%; animation:orbitPulse 3s ease-in-out infinite;
}
.cs-ring-1 .cs-ring-dot { left:-2.5px; margin-top:-2.5px; }
.cs-ring-2 .cs-ring-dot { right:-2.5px; margin-top:-2.5px; }

/* NAV */
.cs-nav {
  position:fixed; top:0; left:0; right:0; z-index:100;
  padding:28px 56px;
  display:flex; align-items:center; justify-content:space-between;
}
.cs-nav::before {
  content:''; position:absolute; inset:0;
  background:linear-gradient(to bottom,rgba(250,249,246,.96) 0%,rgba(250,249,246,0) 100%);
  pointer-events:none;
}
.cs-logo { display:flex; align-items:center; gap:14px; position:relative; z-index:1; }
.cs-logo-mark {
  width:40px; height:40px; border-radius:50%;
  border:1px solid rgba(160,120,32,.35);
  display:flex; align-items:center; justify-content:center;
  background:rgba(160,120,32,.07); position:relative;
  box-shadow:0 2px 12px rgba(160,120,32,.1);
}
.cs-logo-mark::before {
  content:''; position:absolute; inset:3px;
  border-radius:50%; border:1px solid rgba(160,120,32,.2);
}
.cs-logo-mark svg { color:var(--gold); }
.cs-logo-text {}
.cs-logo-name {
  font-family:var(--ff-display); font-size:17px; font-weight:700;
  color:var(--cream); letter-spacing:.02em; display:block;
}
.cs-logo-sub {
  font-size:9px; font-weight:500; letter-spacing:.25em;
  text-transform:uppercase; color:var(--gold); display:block; margin-top:1px;
}

/* PAGE LAYOUT */
.cs-page {
  position:relative; z-index:10;
  min-height:100vh;
  display:flex; flex-direction:column; justify-content:center;
  padding:140px 56px 60px;
  max-width:1200px; margin:0 auto;
  gap:56px;
}

/* HERO — TWO COLUMN */
.cs-hero {
  display:grid;
  grid-template-columns:1fr 1fr;
  gap:80px;
  align-items:center;
}

/* LEFT */
.cs-left { display:flex; flex-direction:column; }
.cs-eyebrow {
  display:flex; align-items:center; gap:12px; margin-bottom:28px;
  opacity:0; animation:fadeUp .7s ease .3s forwards;
}
.cs-eyebrow-line { height:1px; width:36px; background:linear-gradient(to right,var(--gold),transparent); }
.cs-eyebrow-text { font-size:10px; font-weight:500; letter-spacing:.3em; text-transform:uppercase; color:var(--gold); }

.cs-title {
  font-family:var(--ff-display); font-weight:900;
  font-size:clamp(50px,5.5vw,82px); line-height:1.0; letter-spacing:-.02em;
  margin-bottom:28px;
  opacity:0; animation:fadeUp .8s cubic-bezier(.16,1,.3,1) .5s forwards;
}
.cs-title-plain { display:block; color:var(--cream); }
.cs-title-shine {
  display:block;
  background:linear-gradient(110deg,var(--gold-dk) 0%,var(--gold) 30%,var(--gold-lt) 50%,var(--gold) 70%,var(--gold-dk) 100%);
  background-size:200% auto;
  -webkit-background-clip:text; -webkit-text-fill-color:transparent; background-clip:text;
  animation:fadeUp .8s cubic-bezier(.16,1,.3,1) .5s forwards, shimmer 4s linear 1.5s infinite;
}

.cs-desc {
  font-size:15px; font-weight:300; line-height:1.85; color:var(--cream-dim);
  max-width:420px; margin-bottom:44px;
  opacity:0; animation:fadeUp .7s ease .75s forwards;
}

/* FORM */
.cs-form-wrap { opacity:0; animation:fadeUp .7s ease .95s forwards; }
.cs-form-label {
  font-size:9px; font-weight:500; letter-spacing:.22em; text-transform:uppercase;
  color:var(--gold); margin-bottom:12px; display:block;
}
.cs-form {
  display:flex; gap:0;
  border:1px solid rgba(160,120,32,.25); border-radius:3px;
  overflow:hidden; background:rgba(255,255,255,.7); backdrop-filter:blur(12px);
  transition:border-color .3s;
  box-shadow:0 2px 20px rgba(160,120,32,.08), 0 1px 0 rgba(255,255,255,.9) inset;
}
.cs-form:focus-within { border-color:rgba(160,120,32,.6); animation:inputGlow .6s ease forwards; }
.cs-input {
  flex:1; padding:15px 20px; background:transparent; border:none; outline:none;
  color:var(--cream); font-family:var(--ff-body); font-size:14px;
}
.cs-input::placeholder { color:var(--cream-faint); }
.cs-btn {
  padding:15px 26px;
  background:linear-gradient(135deg,var(--gold-dk),var(--gold));
  border:none; color:#fff;
  font-family:var(--ff-body); font-size:11px; font-weight:600;
  letter-spacing:.14em; text-transform:uppercase; cursor:pointer;
  white-space:nowrap; transition:opacity .2s, transform .15s;
  display:flex; align-items:center; gap:8px;
}
.cs-btn:hover  { opacity:.88; }
.cs-btn:active { transform:scale(.98); }
.cs-btn.error  { background:linear-gradient(135deg,#7a2020,#b33); color:#fff; }
.cs-success {
  display:flex; align-items:center; gap:10px;
  padding:15px 20px;
  border:1px solid rgba(160,120,32,.3); border-radius:3px;
  background:rgba(160,120,32,.07); color:var(--gold-dk); font-size:14px;
  font-weight:500;
}

/* TAGS */
.cs-tags {
  display:flex; flex-wrap:wrap; gap:8px; margin-top:28px;
  opacity:0; animation:fadeUp .6s ease 1.1s forwards;
}
.cs-tag {
  padding:5px 12px;
  border:1px solid rgba(0,0,0,.1); border-radius:2px;
  font-size:9px; font-weight:500; letter-spacing:.14em; text-transform:uppercase;
  color:var(--cream-dim); background:rgba(255,255,255,.55);
  transition:border-color .25s,color .25s,background .25s; cursor:default;
  backdrop-filter:blur(6px);
  box-shadow:0 1px 4px rgba(0,0,0,.06);
}
.cs-tag:hover { border-color:rgba(160,120,32,.35); color:var(--gold); background:rgba(255,255,255,.85); }

/* RIGHT */
.cs-right {
  display:flex; flex-direction:column; align-items:center; gap:36px;
  opacity:0; animation:fadeIn 1s ease .6s forwards;
}

/* ORB */
.cs-orb-wrap {
  position:relative; width:280px; height:280px;
  display:flex; align-items:center; justify-content:center;
}
.cs-orb-bg {
  position:absolute; width:210px; height:210px; border-radius:50%;
  background:radial-gradient(circle at 35% 35%,rgba(255,255,255,.95) 0%,rgba(240,232,210,.8) 50%,rgba(220,210,185,.5) 100%);
  border:1px solid rgba(160,120,32,.2);
  box-shadow:
    0 20px 60px rgba(160,120,32,.15),
    0 4px 20px rgba(160,120,32,.1),
    inset 0 1px 0 rgba(255,255,255,1);
  animation:breathe 6s ease-in-out infinite;
}
.cs-orb-ring {
  position:absolute; border-radius:50%; border:1px solid;
}
.cs-orb-r1 { width:235px; height:235px; border-color:rgba(160,120,32,.12); animation:slowRotate    20s linear infinite; }
.cs-orb-r2 { width:268px; height:268px; border-color:rgba(160,120,32,.07); animation:slowRotateRev 30s linear infinite; border-style:dashed; }
.cs-orb-inner {
  position:relative; z-index:2;
  display:flex; flex-direction:column; align-items:center; gap:10px;
}
.cs-orb-icon {
  width:64px; height:64px; border-radius:50%;
  background:linear-gradient(135deg,rgba(255,255,255,.95),rgba(240,228,195,.9));
  border:1px solid rgba(160,120,32,.3);
  display:flex; align-items:center; justify-content:center;
  box-shadow:0 8px 32px rgba(160,120,32,.2), inset 0 1px 0 rgba(255,255,255,1);
}
.cs-orb-icon svg { color:var(--gold); }
.cs-orb-badge {
  font-size:9px; font-weight:500; letter-spacing:.25em;
  text-transform:uppercase; color:var(--gold);
}

/* WAVE BARS */
.cs-wave-section { width:100%; }
.cs-wave-head {
  font-size:9px; font-weight:500; letter-spacing:.22em;
  text-transform:uppercase; color:var(--cream-faint);
  margin-bottom:14px; text-align:center;
}
.cs-bars {
  display:flex; align-items:center; justify-content:center;
  gap:3px; height:56px;
}
.cs-bar {
  flex-shrink:0; width:3px; border-radius:99px;
  transform-origin:center; animation:waveBar ease-in-out infinite;
}

/* COUNTDOWN */
.cs-countdown-section {
  border-top:1px solid rgba(0,0,0,.08); padding-top:40px;
  opacity:0; animation:fadeUp .7s ease 1.2s forwards;
}
.cs-cd-head {
  font-size:9px; font-weight:500; letter-spacing:.28em; text-transform:uppercase;
  color:var(--gold); margin-bottom:22px;
  display:flex; align-items:center; gap:14px;
}
.cs-cd-head::after { content:''; flex:1; height:1px; background:linear-gradient(to right,rgba(160,120,32,.3),transparent); }
.cs-cd-row { display:flex; gap:14px; align-items:flex-start; }
.cs-cd-unit { flex:1; display:flex; flex-direction:column; align-items:center; gap:8px; }
.cs-cd-box {
  width:100%; aspect-ratio:1.15; max-width:100px;
  background:rgba(255,255,255,.9); border:1px solid rgba(160,120,32,.15); border-radius:3px;
  display:flex; align-items:center; justify-content:center;
  font-family:var(--ff-display); font-size:clamp(22px,3vw,36px); font-weight:700; color:var(--cream);
  position:relative; overflow:hidden;
  box-shadow:0 2px 16px rgba(0,0,0,.06), 0 1px 0 rgba(255,255,255,.9) inset, inset 0 0 0 1px rgba(255,255,255,.5);
  backdrop-filter:blur(10px);
}
.cs-cd-box::before { content:''; position:absolute; left:0; right:0; top:50%; height:1px; background:rgba(160,120,32,.07); }
.cs-cd-box::after  { content:''; position:absolute; inset:0; background:linear-gradient(180deg,rgba(255,255,255,.6) 0%,transparent 50%,rgba(0,0,0,.03) 100%); }
.cs-cd-box.flip { animation:flipCard .4s cubic-bezier(.23,1,.32,1); }
.cs-cd-num { position:relative; z-index:1; }
.cs-cd-lbl { font-size:9px; font-weight:500; letter-spacing:.2em; text-transform:uppercase; color:var(--cream-faint); }
.cs-cd-sep {
  display:flex; align-items:flex-start; padding-top:10px;
  font-family:var(--ff-display); font-size:26px;
  color:rgba(160,120,32,.3); line-height:1;
}

/* FOOTER */
.cs-footer {
  display:flex; align-items:center; justify-content:space-between;
  border-top:1px solid rgba(0,0,0,.08); padding-top:36px;
  opacity:0; animation:fadeUp .6s ease 1.4s forwards;
}
.cs-footer-copy { font-size:11px; color:var(--cream-faint); letter-spacing:.04em; }
.cs-footer-copy span { color:var(--gold); }
.cs-footer-links { display:flex; gap:24px; }
.cs-footer-link {
  font-size:10px; color:var(--cream-faint); letter-spacing:.12em;
  text-transform:uppercase; cursor:pointer; text-decoration:none;
  transition:color .2s;
}
.cs-footer-link:hover { color:var(--gold); }

/* RESPONSIVE */
@media (max-width: 900px) {
  .cs-page { padding:120px 28px 50px; gap:44px; }
  .cs-nav  { padding:22px 28px; }
  .cs-hero { grid-template-columns:1fr; gap:52px; }
  .cs-right { order:-1; }
  .cs-orb-wrap { width:220px; height:220px; }
  .cs-orb-bg   { width:164px; height:164px; }
  .cs-orb-r1   { width:180px; height:180px; }
  .cs-orb-r2   { width:210px; height:210px; }
  .cs-title    { font-size:clamp(44px,10vw,70px); }
}
@media (max-width: 560px) {
  .cs-page     { padding:100px 18px 36px; gap:36px; }
  .cs-form     { flex-direction:column; }
  .cs-btn      { justify-content:center; }
  .cs-cd-row   { gap:8px; }
  .cs-cd-sep   { display:none; }
  .cs-footer   { flex-direction:column; gap:14px; text-align:center; }
  .cs-rings    { display:none; }
}
`;

/* ═══════════════════════════════════════
   ICONS
   ═══════════════════════════════════════ */
function MicIcon({ size = 22 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24"
      fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round">
      <path d="M12 2a3 3 0 00-3 3v7a3 3 0 006 0V5a3 3 0 00-3-3z" />
      <path d="M19 10v2a7 7 0 01-14 0v-2" />
      <path d="M12 19v3M9 22h6" />
    </svg>
  );
}

function ArrowIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24"
      fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <path d="M5 12h14M12 5l7 7-7 7" />
    </svg>
  );
}

function CheckCircleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24"
      fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <path d="M22 11.08V12a10 10 0 11-5.93-9.14" />
      <path d="M22 4L12 14.01l-3-3" />
    </svg>
  );
}

/* ═══════════════════════════════════════
   PARTICLES
   ═══════════════════════════════════════ */
function Particles() {
  const items = useMemo(() =>
    Array.from({ length: 30 }, (_, i) => ({
      id:       i,
      left:     `${Math.random() * 100}%`,
      bottom:   `${Math.random() * 15}%`,
      height:   `${Math.random() * 60 + 20}px`,
      width:    `${Math.random() * 1.2 + 0.6}px`,
      color:    GOLD_PALETTE[Math.floor(Math.random() * GOLD_PALETTE.length)],
      dur:      `${Math.random() * 14 + 8}s`,
      delay:    `${Math.random() * 12}s`,
      dx:       `${(Math.random() - 0.5) * 80}px`,
    }))
  , []);

  return (
    <div className="cs-particles" aria-hidden="true">
      {items.map(p => (
        <div key={p.id} className="cs-particle" style={{
          left:              p.left,
          bottom:            p.bottom,
          height:            p.height,
          width:             p.width,
          background:        p.color,
          animationDuration: p.dur,
          animationDelay:    p.delay,
          '--dx':            p.dx,
          opacity:           0.35,
        }} />
      ))}
    </div>
  );
}

/* ═══════════════════════════════════════
   NAV
   ═══════════════════════════════════════ */
function Nav() {
  return (
    <nav className="cs-nav">
      <div className="cs-logo">
        <div className="cs-logo-mark">
          <MicIcon size={18} />
        </div>
        <div className="cs-logo-text">
          <span className="cs-logo-name">Witness Scan</span>
          <span className="cs-logo-sub">Voice Intelligence Platform</span>
        </div>
      </div>
    </nav>
  );
}

/* ═══════════════════════════════════════
   WAVE BARS
   ═══════════════════════════════════════ */
function WaveBars({ count = 38 }) {
  const bars = useMemo(() =>
    Array.from({ length: count }, (_, i) => {
      const pos      = i / count;
      const envelope = 1 - Math.pow(Math.abs(pos - 0.5) * 2, 1.6);
      const minH     = 8  + envelope * 10;
      const maxH     = 22 + envelope * 34;
      const color    = `hsl(${38 + pos * 30}, ${55 + envelope * 25}%, ${42 + envelope * 18}%)`;
      return {
        id:    i,
        minH, maxH,
        color,
        dur:   `${0.6 + Math.random() * 1.2}s`,
        delay: `${Math.random() * 1.4}s`,
      };
    })
  , [count]);

  return (
    <div className="cs-bars">
      {bars.map(b => (
        <div key={b.id} className="cs-bar" style={{
          height:                `${b.maxH}px`,
          background:            b.color,
          animationDuration:     b.dur,
          animationDelay:        b.delay,
          '--min-scale':         b.minH / b.maxH,
          opacity:               0.6 + (b.maxH / 56) * 0.35,
        }} />
      ))}
    </div>
  );
}

/* ═══════════════════════════════════════
   CENTRAL ORB
   ═══════════════════════════════════════ */
function CentralOrb() {
  return (
    <div className="cs-orb-wrap">
      <div className="cs-orb-bg" />
      <div className="cs-orb-ring cs-orb-r1" />
      <div className="cs-orb-ring cs-orb-r2" />
      <div className="cs-orb-inner">
        <div className="cs-orb-icon">
          <MicIcon size={28} />
        </div>
        <span className="cs-orb-badge">Coming Soon</span>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════
   COUNTDOWN
   ═══════════════════════════════════════ */
const pad = n => String(n).padStart(2, '0');

function Countdown({ target }) {
  const [time, setTime] = useState({ d: 0, h: 0, m: 0, s: 0 });
  const prev = useRef({ d: '', h: '', m: '', s: '' });
  const [flip, setFlip] = useState({ d: false, h: false, m: false, s: false });

  useEffect(() => {
    const tick = () => {
      const diff = target - Date.now();
      if (diff <= 0) return;
      const next = {
        d: Math.floor(diff / 86400000),
        h: Math.floor((diff % 86400000) / 3600000),
        m: Math.floor((diff % 3600000) / 60000),
        s: Math.floor((diff % 60000) / 1000),
      };
      const newFlip = {};
      let changed = false;
      for (const k of ['d','h','m','s']) {
        const p = pad(next[k]);
        if (p !== prev.current[k]) { newFlip[k] = true; prev.current[k] = p; changed = true; }
        else { newFlip[k] = false; }
      }
      if (changed) {
        setFlip(newFlip);
        setTime(next);
        setTimeout(() => setFlip({ d:false, h:false, m:false, s:false }), 420);
      }
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [target]);

  const units = [
    { k:'d', label:'Days' },
    { k:'h', label:'Hours' },
    { k:'m', label:'Minutes' },
    { k:'s', label:'Seconds' },
  ];


}

/* ═══════════════════════════════════════
   NOTIFY FORM
   ═══════════════════════════════════════ */
function NotifyForm() {
  const [email, setEmail]       = useState('');
  const [hasError, setHasError] = useState(false);
  const [done, setDone]         = useState(false);

  const submit = () => {
    if (!/\S+@\S+\.\S+/.test(email)) {
      setHasError(true);
      setTimeout(() => setHasError(false), 1400);
      return;
    }
    setDone(true);
  };

  if (done) {
    return (
      <div className="cs-success">
        <CheckCircleIcon />
        You're on the early access list — we'll be in touch.
      </div>
    );
  }


}

/* ═══════════════════════════════════════
   MAIN COMPONENT
   ═══════════════════════════════════════ */
export default function ComingSoon() {
  const target = useRef((() => {
    const d = new Date();
    d.setDate(d.getDate() + LAUNCH_DAYS);
    return d;
  })()).current;

  return (
    <>
      <style>{styles}</style>
      <div className="cs-root">
        {/* Atmosphere */}
        <div className="cs-grid"    aria-hidden="true" />
        <div className="cs-scanline" aria-hidden="true" />
        <Particles />

        {/* Orbiting rings */}
        <div className="cs-rings" aria-hidden="true">
          <div className="cs-ring cs-ring-1"><span className="cs-ring-dot" /></div>
          <div className="cs-ring cs-ring-2"><span className="cs-ring-dot" /></div>
          <div className="cs-ring cs-ring-3" />
        </div>

        {/* Nav */}
        <Nav />

        {/* Page */}
        <main className="cs-page">

          {/* ── HERO ── */}
          <div className="cs-hero">

            {/* LEFT */}
            <div className="cs-left">
              <div className="cs-eyebrow">
                <span className="cs-eyebrow-line" />
                <span className="cs-eyebrow-text">Voice Intelligence · Est. 2026</span>
              </div>

              <h1 className="cs-title">
                <span className="cs-title-plain">Something</span>
                <span className="cs-title-shine">Extraordinary</span>
                <span className="cs-title-plain" style={{ fontStyle:'italic', opacity:0.5 }}>is coming.</span>
              </h1>

              <p className="cs-desc">
                Witness Scan is being forged from the ground up — an AI-native
                platform for precision voice capture, real-time transcription,
                and deep acoustic intelligence.
              </p>

              <NotifyForm />

              <div className="cs-tags">
                {FEATURES.map(f => (
                  <span className="cs-tag" key={f}>{f}</span>
                ))}
              </div>
            </div>

            {/* RIGHT */}
            <div className="cs-right">
              <CentralOrb />

              <div className="cs-wave-section">
                <p className="cs-wave-head">Live Audio Analysis</p>
                <WaveBars count={40} />
              </div>
            </div>
          </div>

          {/* ── COUNTDOWN ── */}
 

          {/* ── FOOTER ── */}
          <footer className="cs-footer">
            <p className="cs-footer-copy">
              © 2026 <span>Witness Scan</span>. All rights reserved.
            </p>
       
          </footer>

        </main>
      </div>
    </>
  );
}